import NorthRoundedIcon from "@mui/icons-material/NorthRounded";
import { Box, Button, IconButton, TextField, Tooltip } from "@mui/material";
import { useFocusWithin } from "hooks/useFocusWithin";
import { useState } from "react";

interface CommentInputProps {
  /** Resolve with `{ clear: false }` to keep the draft in the box (e.g. the
   * user backed out of a confirmation step); any other result clears it. */
  onSubmit: (comment: string) => Promise<void | { clear: boolean }>;
  loading?: boolean;
  placeholder?: string;
  buttonText?: string;
  rows?: number;
  maxRows?: number;
  disabled?: boolean;
  submitOnMetaEnter?: boolean;
  submitButtonVariant?: "text" | "icon";
  /** When true, the submit button (and `footerEnd`) stay hidden until the
   * field is focused or has content — the empty resting state is just the
   * textbox and its placeholder. */
  collapseControlsWhenEmpty?: boolean;
  /** Rendered right-aligned in the footer row, immediately before the submit
   * button (e.g. a "Notify immediately" checkbox). */
  footerEnd?: React.ReactNode;
}

export function CommentInput({
  onSubmit,
  loading = false,
  placeholder = "Add a comment...",
  buttonText = "Add Comment",
  rows = 3,
  maxRows = 8,
  disabled = false,
  submitOnMetaEnter = true,
  submitButtonVariant = "text",
  collapseControlsWhenEmpty = false,
  footerEnd,
}: CommentInputProps) {
  const [comment, setComment] = useState("");
  const { focused, focusProps } = useFocusWithin();
  const submitTooltip = `${buttonText} (Cmd/Ctrl+Enter)`;
  const isSubmitDisabled = !comment.trim() || loading || disabled;
  // Keep controls visible while loading so they don't vanish mid-submit.
  const showControls =
    !collapseControlsWhenEmpty || focused || comment.trim().length > 0 || loading;

  const handleSubmit = async () => {
    if (!comment.trim() || loading) return;

    const result = await onSubmit(comment.trim());
    if (result && result.clear === false) {
      return;
    }
    setComment("");
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Cmd/Ctrl+Enter submits. Plain Enter inserts a newline.
    if (event.key === "Enter" && submitOnMetaEnter && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      handleSubmit();
    } else if (event.key === "Escape") {
      event.preventDefault();
      if (comment.trim()) {
        // If there's content, clear it and keep focus
        setComment("");
        (event.target as HTMLTextAreaElement).focus();
      } else {
        // If no content, let the event bubble up to close the dialog
        // Remove focus from the textarea to allow dialog to handle the escape
        (event.target as HTMLTextAreaElement).blur();
      }
    }
  };

  return (
    // Track focus-within so clicking a footer control doesn't collapse the row.
    <Box sx={{ pb: 1.5 }} {...focusProps}>
      <TextField
        multiline
        minRows={rows}
        maxRows={maxRows}
        fullWidth
        variant="outlined"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={placeholder}
        disabled={loading || disabled}
        onKeyDown={handleKeyDown}
        sx={{
          "& .MuiOutlinedInput-root": {
            padding: 0,
          },
        }}
        slotProps={{
          input: {
            style: {
              resize: "vertical",
              padding: "8px 14px",
              minHeight: `${rows * 24}px`,
            },
            endAdornment:
              submitButtonVariant === "icon" && showControls ? (
                <Box
                  sx={{
                    position: "absolute",
                    right: 8,
                    bottom: 8,
                    display: "flex",
                    alignItems: "center",
                    zIndex: 1,
                    pointerEvents: "auto",
                  }}
                >
                  <Tooltip title={submitTooltip} arrow>
                    <span
                      style={{
                        display: "inline-flex",
                        cursor: isSubmitDisabled ? "default" : "pointer",
                      }}
                    >
                      <IconButton
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled}
                        size="small"
                        aria-label={submitTooltip}
                        sx={{
                          width: 30,
                          height: 30,
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          borderRadius: "50%",
                          color: "text.secondary",
                          backgroundColor: "background.paper",
                          cursor: isSubmitDisabled ? "default" : "pointer",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        <NorthRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ) : undefined,
          },
        }}
      />
      {showControls && (footerEnd || submitButtonVariant === "text") && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
            mt: 0.75,
          }}
        >
          {footerEnd}
          {submitButtonVariant === "text" && (
            <Tooltip title={submitTooltip} arrow>
              <span
                style={{
                  display: "inline-flex",
                  cursor: isSubmitDisabled ? "default" : "pointer",
                }}
              >
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitDisabled}
                  variant="contained"
                  size="small"
                  aria-label={submitTooltip}
                  sx={{
                    cursor: isSubmitDisabled ? "default" : "pointer",
                  }}
                >
                  {loading ? "Adding..." : buttonText}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
}
